import { useState } from "react";

import { Tabs, TabList, TabPanel } from "@similarweb/ui-components";

import translateNested from "app/pages/sales-intelligence/common-components/i18n/translateNested";
import ListPreLoaderItem from "app/pages/sales-intelligence/common-components/ListPreLoaderItem/ListPreLoaderItem";
import { PAGE_SIZE } from "app/pages/sales-intelligence/modules/lists/constants/constants";
import EmptyStaticListContainer from "app/pages/sales-intelligence/modules/lists/containers/list/EmptyStaticListContainer/EmptyStaticListContainer";
import StaticListContainer from "app/pages/sales-intelligence/modules/lists/containers/list/StaticListContainer/StaticListContainer";
import { StyledListTab } from "app/pages/sales-intelligence/modules/lists/containers/list/StaticListContainer/styles";
import StaticListHeaderContainer from "app/pages/sales-intelligence/modules/lists/containers/list/StaticListHeaderContainer/StaticListHeaderContainer";
import SavedSearchesContainer from "app/pages/sales-intelligence/modules/lists/containers/saved-searches/SavedSearchesContainer/SavedSearchesContainer";
import useSavedSearchesList from "app/pages/sales-intelligence/modules/lists/sub-modules/my-list/queries/useSavedSearchsList";
import useStaticList from "app/pages/sales-intelligence/modules/lists/sub-modules/my-list/queries/useStaticListQuery";
import trackingService from "app/pages/sales-intelligence/modules/lists/sub-modules/my-list/services/tracking/trackingService";
import { LIST_TABS, TABS_LIST } from "app/pages/sales-intelligence/pages/lists/constants/constants";

import EmptySavedSearchesContainer from "../saved-searches/EmptySavedSearchesContainer/EmptySavedSearchesContainer";
import SavedSearchesHeaderContainer from "../saved-searches/SavedSearchesHeaderContainer/SavedSearchesHeaderContainer";

import { StyledListContainerWrapper, StyledListTabsContainer, StyledNoData } from "./styles";

const ListTabsContainer = () => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const { data, isLoading, setPage, setSearchText, searchText, refetch, page } = useStaticList({
        initialPage: 1,
        pageSize: PAGE_SIZE,
    });

    const {
        data: savedSearchData,
        isLoading: isSavedSearchesLoading,
        page: savedSearchesPage,
        setPage: setSavedSearchesPage,
        setSearchText: setSavedSearchesSearchText,
        searchText: savedSearchesSearchText,
        refetch: refetchSavedSearches,
    } = useSavedSearchesList({
        initialPage: 1,
        pageSize: PAGE_SIZE,
    });

    const tabsCount = {
        [LIST_TABS.staticList.id]: data?.totalCount || 0,
        [LIST_TABS.savedSearches.id]: savedSearchData?.totalCount || 0,
    };

    const handleSelectTab = (selectedIndex: number): void => {
        trackingService.trackTabsOptionsClicked(TABS_LIST[selectedIndex]);
        setSelectedIndex(selectedIndex);
    };

    const handleStaticListEnterSearchText = (
        setSearchAction: (searchTerm: string) => void,
        setPageAction: (page: number) => void,
        tabIndex: number,
    ) => {
        return (searchTerm: string) => {
            trackingService.trackSearchInTab(TABS_LIST[tabIndex]);
            setSearchAction(searchTerm);
            setPageAction(1);
        };
    };

    return (
        <StyledListTabsContainer>
            <Tabs
                selectedIndex={selectedIndex}
                onSelect={handleSelectTab}
            >
                <TabList>
                    {Object.values(LIST_TABS).map(({ id, title }) => {
                        return (
                            <StyledListTab key={id}>
                                {translateNested({
                                    key: title,
                                    replacements: {
                                        quantity: tabsCount[id],
                                    },
                                })}
                            </StyledListTab>
                        );
                    })}
                </TabList>
                <TabPanel>
                    {isLoading ||
                    (data && (data.totalCount > 0 || (data.totalCount === 0 && searchText.length > 0))) ? (
                        <StaticListHeaderContainer
                            searchText={searchText}
                            setSearchText={handleStaticListEnterSearchText(setSearchText, setPage, 0)}
                        />
                    ) : null}

                    <StyledListContainerWrapper>
                        {isLoading ? <ListPreLoaderItem rowNumber={3} /> : null}
                        {!isLoading && data && data.totalCount > 0 ? (
                            <StaticListContainer
                                records={data.records}
                                totalCount={data.totalCount}
                                setPage={setPage}
                                refetch={refetch}
                                page={page}
                            />
                        ) : null}

                        {!isLoading && data && data.totalCount === 0 && searchText.length > 0 ? (
                            <StyledNoData
                                subtitle=""
                                titleSize={16}
                            />
                        ) : null}
                        {!isLoading && data && data.totalCount === 0 && searchText.length === 0 ? (
                            <EmptyStaticListContainer />
                        ) : null}
                    </StyledListContainerWrapper>
                </TabPanel>
                <TabPanel>
                    {isSavedSearchesLoading ||
                    (savedSearchData &&
                        (savedSearchData.totalCount > 0 ||
                            (savedSearchData.totalCount === 0 && savedSearchesSearchText.length > 0))) ? (
                        <SavedSearchesHeaderContainer
                            searchText={savedSearchesSearchText}
                            setSearchText={handleStaticListEnterSearchText(
                                setSavedSearchesSearchText,
                                setSavedSearchesPage,
                                1,
                            )}
                        />
                    ) : null}
                    <StyledListContainerWrapper>
                        {isSavedSearchesLoading ? <ListPreLoaderItem rowNumber={3} /> : null}
                        {!isSavedSearchesLoading && savedSearchData && savedSearchData.totalCount > 0 ? (
                            <SavedSearchesContainer
                                records={savedSearchData.records}
                                totalCount={savedSearchData.totalCount}
                                setPage={setSavedSearchesPage}
                                refetch={refetchSavedSearches}
                                page={savedSearchesPage}
                            />
                        ) : null}
                        {!isSavedSearchesLoading &&
                        savedSearchData &&
                        savedSearchData.totalCount === 0 &&
                        savedSearchesSearchText.length > 0 ? (
                            <StyledNoData
                                subtitle=""
                                titleSize={16}
                            />
                        ) : null}
                        {!isLoading &&
                        savedSearchData &&
                        savedSearchData.totalCount === 0 &&
                        savedSearchesSearchText.length === 0 ? (
                            <EmptySavedSearchesContainer />
                        ) : null}
                    </StyledListContainerWrapper>
                </TabPanel>
            </Tabs>
        </StyledListTabsContainer>
    );
};

export default ListTabsContainer;
